import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/shared/Common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { UploadSimple, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NewCase() {
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    case_type: "Refund Request",
    customer: "",
    priority: "medium",
    complexity: "moderate",
    required_skill: "Billing",
    estimated_effort_min: 60,
    customer_tier: "standard",
    vulnerable: false,
    sla_hours: 24,
    description: "",
    team: "",
    due_date: "",
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    api.get("/skills").then(r => setSkills(r.data.items)).catch(()=>{});
    api.get("/teams").then(r => setTeams(r.data.items)).catch(()=>{});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.due_date) payload.due_date = new Date(Date.now() + payload.sla_hours * 3600 * 1000).toISOString();
      const { data } = await api.post("/cases", payload);
      toast.success(`Case ${data.case_id} created — allocation running`);
      setTimeout(() => nav(`/cases/${data.id}`), 500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkUpload = async () => {
    if (!file) return toast.error("Choose a CSV file");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await fetch(`${API}/cases/bulk-import`, {
        method: "POST",
        credentials: "include",
        body: fd,
      }).then(r => r.json());
      toast.success(`Imported ${data.created || 0} cases`);
      setFile(null);
    } catch (e) { toast.error("Import failed"); }
  };

  return (
    <div>
      <Button data-testid="new-case-back-btn" variant="ghost" size="sm" onClick={() => nav("/cases")} className="mb-3">
        <ArrowLeft size={14} className="mr-1" /> Back
      </Button>
      <PageHeader title="Create a case" subtitle="Submit new work into the allocation queue" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} data-testid="new-case-form" className="lg:col-span-2 border rounded-md p-6 bg-card space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="eyebrow text-muted-foreground">Case type</Label>
              <Input data-testid="case-type-input" value={form.case_type} onChange={e => set("case_type", e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Customer</Label>
              <Input data-testid="customer-input" value={form.customer} onChange={e => set("customer", e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Priority</Label>
              <Select value={form.priority} onValueChange={v => set("priority", v)}>
                <SelectTrigger data-testid="priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Complexity</Label>
              <Select value={form.complexity} onValueChange={v => set("complexity", v)}>
                <SelectTrigger data-testid="complexity-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["simple", "moderate", "complex", "expert"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Required skill</Label>
              <Select value={form.required_skill} onValueChange={v => set("required_skill", v)}>
                <SelectTrigger data-testid="skill-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{skills.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Team</Label>
              <Select value={form.team} onValueChange={v => set("team", v)}>
                <SelectTrigger data-testid="team-select" className="mt-1.5"><SelectValue placeholder="Any team" /></SelectTrigger>
                <SelectContent>{teams.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Customer tier</Label>
              <Select value={form.customer_tier} onValueChange={v => set("customer_tier", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["standard", "premium", "enterprise"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">Estimated effort (min)</Label>
              <Input type="number" value={form.estimated_effort_min} onChange={e => set("estimated_effort_min", parseInt(e.target.value) || 0)} className="mt-1.5" />
            </div>
            <div>
              <Label className="eyebrow text-muted-foreground">SLA (hours)</Label>
              <Input type="number" value={form.sla_hours} onChange={e => set("sla_hours", parseInt(e.target.value) || 24)} className="mt-1.5" />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex items-center gap-3">
                <Switch data-testid="vulnerable-switch" checked={form.vulnerable} onCheckedChange={v => set("vulnerable", v)} />
                <Label className="eyebrow text-muted-foreground">Vulnerable customer</Label>
              </div>
            </div>
          </div>

          <div>
            <Label className="eyebrow text-muted-foreground">Description</Label>
            <Textarea value={form.description} onChange={e => set("description", e.target.value)} className="mt-1.5 min-h-[100px]" placeholder="Provide case details…" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button data-testid="submit-case-btn" type="submit"><Sparkle size={14} className="mr-1" /> Create & Auto-Allocate</Button>
            <Button type="button" variant="outline" onClick={() => nav("/cases")}>Cancel</Button>
          </div>
        </form>

        <div className="border rounded-md p-6 bg-card h-fit">
          <div className="eyebrow text-muted-foreground mb-2">Bulk import</div>
          <p className="text-sm text-muted-foreground mb-4">Upload a CSV with columns: case_type, customer, priority, complexity, required_skill, estimated_effort_min, customer_tier, sla_hours, description</p>
          <input data-testid="bulk-file-input" type="file" accept=".csv" onChange={e => setFile(e.target.files[0])} className="text-sm mb-3" />
          <Button data-testid="bulk-upload-btn" onClick={bulkUpload} variant="outline" className="w-full">
            <UploadSimple size={14} className="mr-1" /> Import CSV
          </Button>
          <div className="text-[10px] mono text-muted-foreground mt-3">Uploaded cases will be auto-allocated in the background.</div>
        </div>
      </div>
    </div>
  );
}
